<script setup>
import { computed, reactive, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import { useArrowsAiAssistants } from 'dashboard/composables/useArrowsAiAssistants';
import { useUnsavedChangesGuard } from 'dashboard/composables/useUnsavedChangesGuard';
import Button from 'dashboard/components-next/button/Button.vue';
import Editor from 'dashboard/components-next/Editor/Editor.vue';
import RadioCard from 'dashboard/components-next/radioCard/RadioCard.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import SettingsToggleSection from 'dashboard/components-next/Settings/SettingsToggleSection.vue';
import SettingsPageLayout from 'dashboard/components-next/arrowsAi/settings/SettingsPageLayout.vue';
import DurationSelect from 'dashboard/components-next/arrowsAi/settings/DurationSelect.vue';
import MissingAssistant from 'dashboard/components-next/arrowsAi/MissingAssistant.vue';
import UnsavedChangesDialog from 'dashboard/components-next/arrowsAi/UnsavedChangesDialog.vue';
import AccountBotGuard from './AccountBotGuard.vue';

const { t } = useI18n();
const route = useRoute();
const { getAssistant, rememberAssistant, updateAssistant } =
  useArrowsAiAssistants();

const assistant = computed(() => {
  const current = getAssistant(route.params.assistantId);
  if (current) rememberAssistant(current.id);
  return current;
});

const state = reactive({
  autoResolveMode: 'evaluated',
  inactivityThresholdMinutes: 60,
  sendInactivityResolutionMessage: true,
  sendFollowUpMessage: false,
  includeAttachmentUrls: false,
  includeContactInfo: false,
  followUpThresholdMinutes: 15,
  followUpMessage: '',
  handoffMessage: '',
  resolutionMessage: '',
  temperature: 0.7,
});

const modes = computed(() => [
  {
    id: 'evaluated',
    label: t('ARROWS_AI.SETTINGS.INACTIVITY.EVALUATED.LABEL'),
    description: t('ARROWS_AI.SETTINGS.INACTIVITY.EVALUATED.DESCRIPTION'),
  },
  {
    id: 'legacy',
    label: t('ARROWS_AI.SETTINGS.INACTIVITY.LEGACY.LABEL'),
    description: t('ARROWS_AI.SETTINGS.INACTIVITY.LEGACY.DESCRIPTION'),
  },
  {
    id: 'disabled',
    label: t('ARROWS_AI.SETTINGS.INACTIVITY.DISABLED.LABEL'),
    description: t('ARROWS_AI.SETTINGS.INACTIVITY.DISABLED.DESCRIPTION'),
  },
]);

const showDuration = computed(() => state.autoResolveMode !== 'disabled');
const durationLabel = computed(() =>
  state.autoResolveMode === 'evaluated'
    ? t('ARROWS_AI.SETTINGS.INACTIVITY.REVIEW_AFTER')
    : t('ARROWS_AI.SETTINGS.INACTIVITY.RESOLVE_AFTER')
);

const snapshot = ref('');
const serialize = () => JSON.stringify(state);
const isDirty = computed(() => serialize() !== snapshot.value);

watch(
  assistant,
  (current, previous) => {
    if (!current) return;
    if (isDirty.value && previous?.id === current.id) return;
    state.autoResolveMode = current.autoResolveMode;
    state.inactivityThresholdMinutes = current.inactivityThresholdMinutes;
    state.sendInactivityResolutionMessage =
      current.sendInactivityResolutionMessage;
    state.sendFollowUpMessage = current.sendFollowUpMessage;
    state.includeAttachmentUrls = current.includeAttachmentUrls === true;
    state.includeContactInfo = current.includeContactInfo === true;
    state.followUpThresholdMinutes = current.followUpThresholdMinutes;
    state.followUpMessage = current.followUpMessage;
    state.handoffMessage = current.handoffMessage;
    state.resolutionMessage = current.resolutionMessage;
    state.temperature = Number.isFinite(Number(current.temperature))
      ? Number(current.temperature)
      : 0.7;
    snapshot.value = serialize();
  },
  { immediate: true }
);

const persistSettings = async () => {
  if (!assistant.value) {
    useAlert(t('ARROWS_AI.SETTINGS.ERROR'));
    throw new Error('missing-assistant');
  }

  try {
    await updateAssistant(assistant.value.id, { ...state });
  } catch (error) {
    useAlert(error.message || t('ARROWS_AI.SETTINGS.ERROR'));
    throw error;
  }
  snapshot.value = serialize();
  useAlert(t('ARROWS_AI.SETTINGS.SUCCESS', { name: assistant.value.name }));
};

const handleSave = () => persistSettings().catch(() => {});
const { dialogRef, confirmSave, discard, stay } = useUnsavedChangesGuard({
  isDirty,
  save: persistSettings,
});
</script>

<template>
  <SettingsPageLayout
    :heading="t('ARROWS_AI.SETTINGS.SYSTEM.TITLE')"
    :description="t('ARROWS_AI.SETTINGS.SYSTEM.DESCRIPTION')"
  >
    <MissingAssistant v-if="!assistant" />
    <div v-else class="flex flex-col gap-6">
      <SettingsToggleSection
        hide-toggle
        :header="t('ARROWS_AI.SETTINGS.INACTIVITY.TITLE')"
        :description="t('ARROWS_AI.SETTINGS.INACTIVITY.DESCRIPTION')"
      >
        <div class="flex flex-col w-full gap-4 pt-3">
          <div class="flex flex-col gap-3 px-4">
            <RadioCard
              v-for="mode in modes"
              :id="mode.id"
              :key="mode.id"
              :label="mode.label"
              :description="mode.description"
              :is-active="state.autoResolveMode === mode.id"
              active-outline="outline-n-iris-9"
              @select="state.autoResolveMode = $event"
            />
          </div>
          <div
            v-if="showDuration"
            class="flex flex-col gap-3 px-4 pt-4 border-t border-n-weak"
          >
            <div
              class="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between"
            >
              <span class="font-medium text-n-slate-12">
                {{ durationLabel }}
              </span>
              <DurationSelect
                v-model="state.inactivityThresholdMinutes"
                :hours-aria-label="
                  t('ARROWS_AI.SETTINGS.INACTIVITY.HOURS_ARIA')
                "
                :minutes-aria-label="
                  t('ARROWS_AI.SETTINGS.INACTIVITY.MINUTES_ARIA')
                "
              />
            </div>
            <div
              class="flex items-start justify-between gap-3 pt-4 border-t border-n-weak"
            >
              <div class="flex flex-col gap-1">
                <span class="font-medium text-n-slate-12">
                  {{ t('ARROWS_AI.SETTINGS.INACTIVITY.RESOLUTION_TOGGLE') }}
                </span>
                <span class="text-sm text-n-slate-11">
                  {{
                    t('ARROWS_AI.SETTINGS.INACTIVITY.RESOLUTION_TOGGLE_DESC')
                  }}
                </span>
              </div>
              <Switch v-model="state.sendInactivityResolutionMessage" />
            </div>
          </div>
        </div>
        <template
          v-if="showDuration && state.sendInactivityResolutionMessage"
          #editor
        >
          <Editor
            v-model="state.resolutionMessage"
            :placeholder="
              t('ARROWS_AI.SETTINGS.INACTIVITY.RESOLUTION_PLACEHOLDER')
            "
            :max-length="200"
            :enable-canned-responses="false"
          />
        </template>
      </SettingsToggleSection>
      <SettingsToggleSection
        v-model="state.sendFollowUpMessage"
        :header="t('ARROWS_AI.SETTINGS.INACTIVITY.FOLLOW_UP_TOGGLE')"
        :description="t('ARROWS_AI.SETTINGS.INACTIVITY.FOLLOW_UP_TOGGLE_DESC')"
      >
        <div
          v-if="state.sendFollowUpMessage"
          class="flex flex-col gap-3 px-4 pt-3 lg:flex-row lg:items-center lg:justify-between"
        >
          <span class="font-medium text-n-slate-12">
            {{ t('ARROWS_AI.SETTINGS.INACTIVITY.FOLLOW_UP_AFTER') }}
          </span>
          <DurationSelect
            v-model="state.followUpThresholdMinutes"
            :hours-aria-label="t('ARROWS_AI.SETTINGS.INACTIVITY.HOURS_ARIA')"
            :minutes-aria-label="
              t('ARROWS_AI.SETTINGS.INACTIVITY.MINUTES_ARIA')
            "
          />
        </div>
        <template v-if="state.sendFollowUpMessage" #editor>
          <Editor
            v-model="state.followUpMessage"
            :placeholder="
              t('ARROWS_AI.SETTINGS.INACTIVITY.FOLLOW_UP_PLACEHOLDER')
            "
            :max-length="200"
            :enable-canned-responses="false"
          />
        </template>
      </SettingsToggleSection>
      <SettingsToggleSection
        hide-toggle
        :header="t('ARROWS_AI.SETTINGS.INACTIVITY.HANDOFF')"
        :description="t('ARROWS_AI.SETTINGS.INACTIVITY.HANDOFF_DESC')"
      >
        <template #editor>
          <Editor
            v-model="state.handoffMessage"
            :placeholder="
              t('ARROWS_AI.SETTINGS.INACTIVITY.HANDOFF_PLACEHOLDER')
            "
            :max-length="200"
            :enable-canned-responses="false"
          />
        </template>
      </SettingsToggleSection>
      <SettingsToggleSection
        hide-toggle
        :header="t('ARROWS_AI.SETTINGS.TEMPERATURE.TITLE')"
        :description="t('ARROWS_AI.SETTINGS.TEMPERATURE.DESCRIPTION')"
      >
        <div class="flex flex-col gap-2 px-4 pt-3">
          <div class="flex items-center gap-3">
            <span class="text-xs text-n-slate-11 shrink-0">
              {{ t('ARROWS_AI.SETTINGS.TEMPERATURE.PRECISE') }}
            </span>
            <input
              v-model.number="state.temperature"
              type="range"
              min="0"
              max="1"
              step="0.1"
              class="flex-1 h-1 rounded-full appearance-none cursor-pointer bg-n-slate-6 accent-n-brand [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:size-3 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-n-brand [&::-moz-range-thumb]:appearance-none [&::-moz-range-thumb]:size-3 [&::-moz-range-thumb]:border-0 [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:bg-n-brand"
            />
            <span class="text-xs text-n-slate-11 shrink-0">
              {{ t('ARROWS_AI.SETTINGS.TEMPERATURE.CREATIVE') }}
            </span>
          </div>
          <span class="text-sm font-medium text-center text-n-slate-12">
            {{ Number(state.temperature).toFixed(1) }}
          </span>
        </div>
      </SettingsToggleSection>
      <SettingsToggleSection
        v-model="state.includeAttachmentUrls"
        :header="t('ARROWS_AI.SETTINGS.ATTACHMENT_URLS.TITLE')"
        :description="t('ARROWS_AI.SETTINGS.ATTACHMENT_URLS.DESCRIPTION')"
      />
      <SettingsToggleSection
        v-model="state.includeContactInfo"
        :header="t('ARROWS_AI.SETTINGS.CONTACT_INFO.TITLE')"
        :description="t('ARROWS_AI.SETTINGS.CONTACT_INFO.DESCRIPTION')"
      />
      <AccountBotGuard />
      <div>
        <Button :label="t('ARROWS_AI.SAVE')" @click="handleSave" />
      </div>
    </div>
  </SettingsPageLayout>
  <UnsavedChangesDialog
    ref="dialogRef"
    @save="confirmSave"
    @discard="discard"
    @stay="stay"
  />
</template>
